import { tr, enGB } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { fmt, longDate } from "@/lib/booking";
import { getLang, t } from "@/lib/i18n";

const toDate = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function DateStep({ value, onChange, avail, persons, error }) {
  const days = avail.days;
  const byDate = Object.fromEntries(days.map((d) => [d.date, d]));
  const unavailable = (d) => {
    const x = byDate[toIso(d)];
    return !x || x.closed || x.remaining < Math.max(persons, 1);
  };
  const few = days.filter((d) => !d.closed && d.remaining > 0 && d.remaining < avail.capacity * 0.1).map((d) => toDate(d.date));
  const sel = value ? byDate[value] : null;
  const en = getLang() === "en";

  return (
    <div className="grid gap-8 md:grid-cols-[auto_1fr] md:items-start" data-testid="booking-date-step">
      <div className="rounded-2xl border border-line bg-white p-2 sm:p-4 w-fit max-w-full overflow-x-auto">
        <Calendar
          mode="single"
          selected={value ? toDate(value) : undefined}
          onSelect={(d) => d && onChange(toIso(d))}
          disabled={unavailable}
          fromDate={toDate(days[0].date)}
          toDate={toDate(days[days.length - 1].date)}
          defaultMonth={value ? toDate(value) : toDate(days[0].date)}
          locale={en ? enGB : tr}
          weekStartsOn={1}
          showOutsideDays={false}
          modifiers={{ few }}
          modifiersClassNames={{ few: "day-few" }}
          labels={en ? undefined : { labelPrevious: () => "Önceki ay", labelNext: () => "Sonraki ay" }}
          classNames={{
            caption_label: "text-base font-semibold text-forest capitalize",
            nav_button: "inline-flex items-center justify-center h-10 w-10 rounded-full border border-line text-forest hover:bg-cream-2 disabled:opacity-30",
            nav_button_previous: "absolute left-0",
            nav_button_next: "absolute right-0",
            head_cell: "w-11 text-xs font-semibold text-ink-muted uppercase",
            day: "relative h-11 w-11 rounded-full text-[15px] font-medium text-ink hover:bg-cream-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-honey",
            day_selected: "!bg-forest !text-cream hover:!bg-forest",
            day_today: "underline underline-offset-4",
            day_disabled: "!text-stone-500 line-through hover:!bg-transparent cursor-not-allowed",
          }}
          data-testid="booking-calendar"
        />
      </div>
      <div className="space-y-4">
        <ul className="space-y-2 text-sm text-ink-muted">
          <li className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-forest" aria-hidden="true" /> {t("bilet.date.selected")}</li>
          <li className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-[#B45309]" aria-hidden="true" /> {t("bilet.date.few")}</li>
          <li className="flex items-center gap-2"><span className="text-stone-500 line-through font-medium" aria-hidden="true">17</span> {t("bilet.date.full")}</li>
        </ul>
        <div className="rounded-2xl bg-cream-2 p-5" aria-live="polite" data-testid="booking-date-selected">
          {sel ? (
            <>
              <p className="font-serif text-2xl text-forest capitalize">{longDate(sel.date)}</p>
              <p className="text-sm text-ink-muted mt-1" data-testid="booking-date-remaining">{fmt(t("bilet.date.remaining"), { n: sel.remaining.toLocaleString("tr-TR") })}</p>
            </>
          ) : (
            <p className="text-ink-muted">{t("bilet.date.none")}</p>
          )}
        </div>
        {error && <p role="alert" className="text-sm text-[#B42318]" data-testid="booking-date-error">{error}</p>}
      </div>
    </div>
  );
}
